import { createSelector } from 'reselect'
import moment from 'moment'
import { chain, partialRight, get, isEmpty } from 'lodash'
import changeCaseObject from 'change-case-object'
import {
  selectBuyerId,
  selectBuyerInfoOnboardingState,
  selectHasBuyerInfoAcknolwedgedMarketTemp
} from 'src/store/selectors/buyerInfo'
import { BUYER_ONBOARDED, PRIORITIES } from 'src/constants/client/onboarding'
import { EXPLORE_MARKET_TABS } from 'src/constants/navigation'
import { SERVER_DATE } from 'src/constants/formats'
import { QUESTION_REQUESTED_PREQUAL } from 'src/constants/affordability'
import { QUESTION_ACKNOWLEDGED_MARKET_TEMP } from 'src/constants/zip_modal'
import { QUESTION_BUYER_ADD_HOME } from 'src/constants/client/answers'
import { selectHasSelfAddHomeFeature } from 'src/store/selectors/customerProfile'
import { getHomeId } from './home'

export const selectFromClientData = path =>
  createSelector(
    state => state.client.data,
    partialRight(get, path)
  )

export const selectClientFetchId = state => state.client.fetchId

export const selectClientId = createSelector(
  state => state.client.data.id,
  selectBuyerId,
  (clientId, buyerId) => clientId || buyerId
)

export const selectClientLegacyId = selectFromClientData('legacyRef')
export const selectClientIsBuyer = selectFromClientData('hbbAccess')
export const selectClientLocale = selectFromClientData('locale')
export const selectClientCustomerProfile = selectFromClientData(
  'customerProfile'
)

export const selectClientHomes = state => state.client.data.homes

export const selectClientOnboardingState = state =>
  state.client.data.onboardingState

export const selectHasRequestedClient = state => state.client.hasRequestedClient

const selectLoadingClient = state => state.client.loadingData

export const selectHasRequestedHomes = state => state.client.hasRequestedHomes

const selectLoadingHomes = state => state.client.loadingHomes

export const selectLoadingState = state =>
  // has not yet requested OR is loading
  !selectHasRequestedClient(state) ||
  selectLoadingClient(state) ||
  (!selectHasRequestedHomes(state) || selectLoadingHomes(state))

export const selectOnboardingState = createSelector(
  selectClientOnboardingState,
  selectBuyerInfoOnboardingState,
  (clientOB, buyerOB) => clientOB || buyerOB
)

export const selectNeedsOnboarding = createSelector(
  selectOnboardingState,
  onboarding => Boolean(onboarding && onboarding !== BUYER_ONBOARDED)
)

const getSelectFromAnswers = field =>
  createSelector(
    selectFromClientData('clientAnswers'),
    answers => {
      if (isEmpty(answers)) {
        return []
      }

      const answer = answers.find(({ question: q }) => q === field)

      return get(answer, 'response.value')
    }
  )

export const selectHasDeclinedAddHome = createSelector(
  getSelectFromAnswers(QUESTION_BUYER_ADD_HOME),
  value => Boolean(value) && !Array.isArray(value)
)

export const selectHasRequestedPrequal = createSelector(
  getSelectFromAnswers(QUESTION_REQUESTED_PREQUAL),
  value => Boolean(value) && !Array.isArray(value)
)

const selectHasClientAcknolwedgedMarketTemp = createSelector(
  getSelectFromAnswers(QUESTION_ACKNOWLEDGED_MARKET_TEMP),
  value => Boolean(value) && !Array.isArray(value)
)

export const selectHasAcknolwedgedMarketTemp = createSelector(
  selectHasClientAcknolwedgedMarketTemp,
  selectHasBuyerInfoAcknolwedgedMarketTemp,
  (one, two) => one || two
)

export const selectAnswerCity = getSelectFromAnswers('buyer_city')

export const selectAnswerCityName = createSelector(
  selectAnswerCity,
  value => {
    const name = get(value, 'name')

    if (!name || !name.split) {
      return undefined
    }

    return name.split(',')[0]
  }
)

export const selectAnswerCityCenter = createSelector(
  selectAnswerCity,
  ({ lng, lat }) => ({ lng, lat })
)

export const selectAnswerDate = createSelector(
  getSelectFromAnswers('buyer_close_date'),
  date => date && moment(date, SERVER_DATE)
)

export const selectAnswerGoal = getSelectFromAnswers('buyer_goals')

export const selectAnswerMattersMost = createSelector(
  getSelectFromAnswers('buyer_matters_most'),
  values => new Set(changeCaseObject.snakeCase(values))
)

export const selectAnswerInsuranceFeedback = createSelector(
  getSelectFromAnswers('home_insurance_opt_out'),
  result => (Array.isArray(result) ? undefined : Boolean(result))
)

export const selectSortFieldFromAnswers = createSelector(
  selectAnswerMattersMost,
  priorities => {
    // Budget trumps all
    if (priorities.has(PRIORITIES.PRIORITY_BUDGET)) {
      return EXPLORE_MARKET_TABS.CHEAPER_BUYS
    }

    // Desirable neighborhood trumps all but Budget
    if (priorities.has(PRIORITIES.PRIORITY_NEIGHBORHOOD)) {
      return EXPLORE_MARKET_TABS.POPULAR_MARKETS
    }

    // Return on Investment trumps all but Budget & Desirable Neighborhood
    if (priorities.has(PRIORITIES.PRIORITY_INVESTMENT)) {
      return EXPLORE_MARKET_TABS.POPULAR_MARKETS
    }

    // Maximum Sq Ft trumps all but Budget, Desirable Neighborhood, and ROI
    if (priorities.has(PRIORITIES.PRIORITY_SQFT)) {
      return EXPLORE_MARKET_TABS.BIGGER_HOUSE
    }

    // Right Timing trumps only AirBnb, Money Down, and Outdoor
    if (priorities.has(PRIORITIES.PRIORITY_TIMING)) {
      return EXPLORE_MARKET_TABS.FASTEST_MOVE
    }

    // default
    return EXPLORE_MARKET_TABS.CHEAPER_BUYS
  }
)

export const selectShouldPrioritizeAirDna = createSelector(
  selectAnswerMattersMost,
  priorities => {
    if (
      priorities.has(PRIORITIES.PRIORITY_RENTAL) &&
      priorities.has(PRIORITIES.PRIORITY_PARKS) &&
      priorities.has(PRIORITIES.PRIORITY_DOWNPAYMENT)
    ) {
      return true
    }

    return false
  }
)

export const selectIsHomeowner = createSelector(
  selectHasRequestedHomes,
  selectLoadingHomes,
  selectClientHomes,
  (requested, loading, homes) =>
    requested && !loading && (homes || []).length > 0
)

export const selectCreatedAt = createSelector(
  selectFromClientData('createdAt'),
  date => date && moment(date)
)

export const selectDigestClickedAt = createSelector(
  selectFromClientData('digestClickedAt'),
  date => date && moment(date)
)

const onboardingReleaseDate = moment('2019-05-30', 'YYYY-MM-DD')

export const selectWelcomeBack = createSelector(
  selectDigestClickedAt,
  selectCreatedAt,
  (viewed, created) =>
    viewed &&
    viewed.isValid() &&
    created &&
    created.isValid() &&
    created.isBefore(onboardingReleaseDate)
)

export const selectClientHasHomes = createSelector(
  selectHasRequestedHomes,
  selectClientHomes,
  (requested, homes) => requested && homes && homes.length
)

export const selectClientFirstHome = createSelector(
  selectClientHomes,
  partialRight(get, '0')
)

export const selectClientFirstHomeId = createSelector(
  selectClientFirstHome,
  getHomeId
)

export const selectClientHomeZipcodes = createSelector(
  selectClientHomes,
  homes =>
    chain(homes)
      .mapValues('address.zip')
      .values()
      .value()
)

export const selectShowBuyerAddHome = createSelector(
  selectHasSelfAddHomeFeature,
  selectClientHasHomes,
  selectHasDeclinedAddHome,
  (hasFeature, hasHomes, declinedAddHome) =>
    hasFeature && !hasHomes && !declinedAddHome
)
